import { getSupabaseBrowserClient } from '../lib/supabase'

export type OnboardingAnswerState = 'yes' | 'no' | 'not_sure' | 'skipped' | null
export type OnboardingStepState = 'not_started' | 'in_progress' | 'complete' | 'skipped'
export type PreferredStart =
  | 'discover_existing_ai'
  | 'bring_known_uses'
  | 'establish_strategy'
  | 'capture_existing_strategy'
  | 'not_sure'
  | null

export type OnboardingPerson = {
  membershipId: string
  displayName: string
  status: string
  roles: string[]
}

export type PendingAccountabilityInvitation = {
  invitationId: string
  displayName: string | null
  email: string
  status: string
  expiresAt: string
}

export type OnboardingProduct = {
  productId: string
  productName: string
  supplierName: string | null
  description: string | null
  categoryNames: string[]
  categoryCodes: string[]
  selected: boolean
  hasActiveAiUse: boolean
}

export type OnboardingState = {
  organisation: {
    id: string
    name: string
    sector: string | null
    sizeBand: string | null
    countryCode: string
    governanceContactEmail: string | null
    locality: string | null
    stateRegion: string | null
    onboardingStatus: string
  }
  canEdit: boolean
  canManageAccountability: boolean
  governanceOwner: {
    membershipId: string
    displayName: string
    membershipStatus: string
    active: boolean
  } | null
  governanceOwnerNeedsReassignment: boolean
  governanceOwnerInvitation: PendingAccountabilityInvitation | null
  executiveSponsor: {
    membershipId: string
    displayName: string
    membershipStatus: string
    active: boolean
  } | null
  executiveSponsorInvitation: PendingAccountabilityInvitation | null
  people: OnboardingPerson[]
  setup: {
    currentStep: number
    aiPolicyStatus: OnboardingAnswerState
    aiStrategyStatus: OnboardingAnswerState
    aiRegisterStatus: OnboardingAnswerState
    governanceProcessStatus: OnboardingAnswerState
    approvalAuthoritiesStatus: 'known' | 'not_established' | 'not_sure' | 'skipped' | null
    preferredStart: PreferredStart
    businessAreasStepStatus: OnboardingStepState
    technologyStepStatus: OnboardingStepState
    startedAt: string | null
    initialCompletedAt: string | null
    lastCompletedAt: string | null
  }
  businessAreas: Array<{ id: string; name: string; code?: string | null }>
  selectedProducts: Array<{ productId: string; productName: string; supplierName: string | null }>
}

export type OnboardingCategory = { code: string; name: string }

export type ProductAiOffering = {
  offeringId: string
  offeringName: string
  usageStatus: 'using' | 'planning' | 'not_using' | 'not_sure' | null
  confirmedAt: string | null
  capabilities: Array<{ code: string; name: string }>
}

function throwIfError(error: { message: string } | null) {
  if (error) throw new Error(error.message)
}

export async function loadOnboardingState(organisationId: string): Promise<OnboardingState> {
  const supabase = getSupabaseBrowserClient()
  const [stateResult, accountabilityResult, profileResult] = await Promise.all([
    supabase.rpc('get_organisation_onboarding_state', {
      p_organisation_id: organisationId,
    }),
    supabase.rpc('get_organisation_onboarding_accountability_state', {
      p_organisation_id: organisationId,
    }),
    supabase.rpc('get_organisation_onboarding_profile', {
      p_organisation_id: organisationId,
    }),
  ])
  throwIfError(stateResult.error)
  throwIfError(accountabilityResult.error)
  throwIfError(profileResult.error)

  const state = stateResult.data as unknown as Omit<
    OnboardingState,
    'governanceOwnerInvitation' | 'executiveSponsorInvitation'
  >
  const accountability = accountabilityResult.data as unknown as {
    governanceOwnerInvitation: PendingAccountabilityInvitation | null
    executiveSponsorInvitation: PendingAccountabilityInvitation | null
  }
  const profile = profileResult.data as unknown as {
    governanceContactEmail: string | null
    locality: string | null
    stateRegion: string | null
    countryCode: string
  }

  return {
    ...state,
    organisation: {
      ...state.organisation,
      governanceContactEmail: profile?.governanceContactEmail ?? null,
      locality: profile?.locality ?? null,
      stateRegion: profile?.stateRegion ?? null,
      countryCode: profile?.countryCode ?? state.organisation.countryCode,
    },
    governanceOwnerInvitation: accountability?.governanceOwnerInvitation ?? null,
    executiveSponsorInvitation: accountability?.executiveSponsorInvitation ?? null,
  }
}

export async function startOrResumeOnboarding(organisationId: string, currentStep?: number) {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('start_or_resume_organisation_onboarding', {
    p_organisation_id: organisationId,
    p_current_step: currentStep ?? null,
  })
  throwIfError(error)
  return data
}

export async function saveOnboardingSetup(input: {
  organisationId: string
  currentStep: number
  aiPolicyStatus: OnboardingAnswerState
  aiStrategyStatus: OnboardingAnswerState
  aiRegisterStatus: OnboardingAnswerState
  governanceProcessStatus: OnboardingAnswerState
  approvalAuthoritiesStatus: OnboardingState['setup']['approvalAuthoritiesStatus']
  preferredStart: PreferredStart
  businessAreasStepStatus: OnboardingStepState
  technologyStepStatus: OnboardingStepState
}) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('save_organisation_onboarding_setup', {
    p_organisation_id: input.organisationId,
    p_current_step: input.currentStep,
    p_ai_policy_status: input.aiPolicyStatus,
    p_ai_strategy_status: input.aiStrategyStatus,
    p_ai_register_status: input.aiRegisterStatus,
    p_governance_process_status: input.governanceProcessStatus,
    p_approval_authorities_status: input.approvalAuthoritiesStatus,
    p_preferred_start: input.preferredStart,
    p_business_areas_step_status: input.businessAreasStepStatus,
    p_technology_step_status: input.technologyStepStatus,
  })
  throwIfError(error)
}

export async function updateOnboardingOrganisationProfile(input: {
  organisationId: string
  name: string
  governanceContactEmail: string
  locality: string
  stateRegion: string
  countryCode: string
}) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('update_organisation_onboarding_profile', {
    p_organisation_id: input.organisationId,
    p_name: input.name,
    p_governance_contact_email: input.governanceContactEmail,
    p_locality: input.locality,
    p_state_region: input.stateRegion,
    p_country_code: input.countryCode,
  })
  throwIfError(error)
}

export async function setGovernanceOwner(organisationId: string, membershipId: string) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('set_organisation_governance_owner', {
    p_organisation_id: organisationId,
    p_membership_id: membershipId,
  })
  throwIfError(error)
}

export async function setExecutiveSponsor(organisationId: string, membershipId: string | null) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('set_organisation_executive_sponsor', {
    p_organisation_id: organisationId,
    p_membership_id: membershipId,
  })
  throwIfError(error)
}

export async function setOnboardingAccountabilityInvitation(input: {
  organisationId: string
  accountabilityRole: 'governance_owner' | 'executive_sponsor'
  invitationId: string | null
  displayName?: string | null
}) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('set_organisation_onboarding_accountability_invitation', {
    p_organisation_id: input.organisationId,
    p_accountability_role: input.accountabilityRole,
    p_invitation_id: input.invitationId,
    p_display_name: input.displayName ?? null,
  })
  throwIfError(error)
}

export async function createBusinessArea(organisationId: string, name: string) {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('create_discovery_business_unit', {
    p_organisation_id: organisationId,
    p_name: name,
  })
  throwIfError(error)
  return data as string
}

export async function retireBusinessArea(organisationId: string, businessAreaId: string) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('retire_organisation_business_unit', {
    p_organisation_id: organisationId,
    p_business_unit_id: businessAreaId,
  })
  throwIfError(error)
}

export async function listOnboardingCategories(organisationId: string): Promise<OnboardingCategory[]> {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('list_onboarding_business_system_categories', {
    p_organisation_id: organisationId,
  })
  throwIfError(error)
  return (data ?? []) as unknown as OnboardingCategory[]
}

export async function listOnboardingProducts(input: {
  organisationId: string
  query?: string
  categoryCode?: string
}): Promise<OnboardingProduct[]> {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('list_onboarding_business_system_catalogue', {
    p_organisation_id: input.organisationId,
    p_query: input.query ?? null,
    p_category_code: input.categoryCode ?? null,
  })
  throwIfError(error)
  return (data ?? []) as unknown as OnboardingProduct[]
}

export async function setOnboardingProduct(organisationId: string, productId: string, selected: boolean) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('set_organisation_onboarding_product', {
    p_organisation_id: organisationId,
    p_ai_product_id: productId,
    p_selected: selected,
  })
  throwIfError(error)
}

export async function completeOnboarding(organisationId: string): Promise<OnboardingState> {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('complete_streamlined_organisation_onboarding', {
    p_organisation_id: organisationId,
  })
  throwIfError(error)
  return loadOnboardingState(organisationId)
}

export async function listProductAiOfferings(
  organisationId: string,
  productId: string,
): Promise<ProductAiOffering[]> {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('list_product_ai_offerings_for_discovery', {
    p_organisation_id: organisationId,
    p_ai_product_id: productId,
  })
  throwIfError(error)
  return (data ?? []) as unknown as ProductAiOffering[]
}

export async function setProductAiOfferingStatus(input: {
  organisationId: string
  productId: string
  offeringId: string
  usageStatus: 'using' | 'planning' | 'not_using' | 'not_sure'
}) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('set_organisation_ai_offering_status', {
    p_organisation_id: input.organisationId,
    p_ai_product_id: input.productId,
    p_ai_offering_id: input.offeringId,
    p_usage_status: input.usageStatus,
  })
  throwIfError(error)
}
